import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ErrorAction, OkAction } from './baseActions';
import { WorkspaceKey } from '../types/resources';
import { workspaceKeyService } from '../services/workspaceKeyAPI';
import { formatApiErrorPayload } from './utils';
import { selectCurrentWorkspaceId } from './utils/selectors';

type AppState = import('~/store').AppState;

export enum WorkspaceKeysActionType {
  SILENT_GET_WORKSPACE_KEYS = 'SILENT_GET_WORKSPACE_KEYS',
  GET_WORKSPACE_KEYS = 'GET_WORKSPACE_KEYS',
  GET_WORKSPACE_KEYS_OK = 'GET_WORKSPACE_KEYS_OK',
  GET_WORKSPACE_KEYS_ERROR = 'GET_WORKSPACE_KEYS_ERROR',
  DELETE_WORKSPACE_KEYS = 'DELETE_WORKSPACE_KEYS',
  DELETE_WORKSPACE_KEYS_OK = 'DELETE_WORKSPACE_KEYS_OK',
  DELETE_WORKSPACE_KEYS_ERROR = 'DELETE_WORKSPACE_KEYS_ERROR',
  GET_WORKSPACE_KEY = 'GET_WORKSPACE_KEY',
  GET_WORKSPACE_KEY_OK = 'GET_WORKSPACE_KEY_OK',
  GET_WORKSPACE_KEY_ERROR = 'GET_WORKSPACE_KEY_ERROR',
  CREATE_WORKSPACE_KEY = 'CREATE_WORKSPACE_KEY',
  CREATE_WORKSPACE_KEY_OK = 'CREATE_WORKSPACE_KEY_OK',
  CREATE_WORKSPACE_KEY_ERROR = 'CREATE_WORKSPACE_KEY_ERROR',
  DELETE_WORKSPACE_KEY = 'DELETE_WORKSPACE_KEY',
  DELETE_WORKSPACE_KEY_OK = 'DELETE_WORKSPACE_KEY_OK',
  DELETE_WORKSPACE_KEY_ERROR = 'DELETE_WORKSPACE_KEY_ERROR',
  EDIT_WORKSPACE_KEY = 'EDIT_WORKSPACE_KEY',
  EDIT_WORKSPACE_KEY_OK = 'EDIT_WORKSPACE_KEY_OK',
  EDIT_WORKSPACE_KEY_ERROR = 'EDIT_WORKSPACE_KEY_ERROR',
}

export type WithKeyId = { keyId: string };

type GetWorkspaceKeysAction =
  | Action<WorkspaceKeysActionType.GET_WORKSPACE_KEYS>
  | Action<WorkspaceKeysActionType.SILENT_GET_WORKSPACE_KEYS>
  | OkAction<WorkspaceKeysActionType.GET_WORKSPACE_KEYS_OK, WorkspaceKey[]>
  | ErrorAction<WorkspaceKeysActionType.GET_WORKSPACE_KEYS_ERROR>;

type DeleteWorkspaceKeysAction =
  | Action<WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS>
  | OkAction<WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS_OK, WorkspaceKey>
  | ErrorAction<WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS_ERROR>;

type GetWorkspaceKeyAction =
  | (Action<WorkspaceKeysActionType.GET_WORKSPACE_KEY> & WithKeyId)
  | (OkAction<WorkspaceKeysActionType.GET_WORKSPACE_KEY_OK, WorkspaceKey> & WithKeyId)
  | (ErrorAction<WorkspaceKeysActionType.GET_WORKSPACE_KEY_ERROR> & WithKeyId);

type EditWorkspaceKeyAction =
  | (Action<WorkspaceKeysActionType.EDIT_WORKSPACE_KEY> & WithKeyId)
  | (OkAction<WorkspaceKeysActionType.EDIT_WORKSPACE_KEY_OK, WorkspaceKey> & WithKeyId)
  | (ErrorAction<WorkspaceKeysActionType.EDIT_WORKSPACE_KEY_ERROR> & WithKeyId);

type CreateWorkspaceKeyAction =
  | Action<WorkspaceKeysActionType.CREATE_WORKSPACE_KEY>
  | OkAction<WorkspaceKeysActionType.CREATE_WORKSPACE_KEY_OK, WorkspaceKey>
  | ErrorAction<WorkspaceKeysActionType.CREATE_WORKSPACE_KEY_ERROR>;

type DeleteWorkspaceKeyAction =
  | (Action<WorkspaceKeysActionType.DELETE_WORKSPACE_KEY> & WithKeyId)
  | (OkAction<WorkspaceKeysActionType.DELETE_WORKSPACE_KEY_OK, WorkspaceKey> & WithKeyId)
  | (ErrorAction<WorkspaceKeysActionType.DELETE_WORKSPACE_KEY_ERROR> & WithKeyId);

export type WorkspaceKeysAction =
  | GetWorkspaceKeysAction
  | DeleteWorkspaceKeysAction
  | GetWorkspaceKeyAction
  | EditWorkspaceKeyAction
  | CreateWorkspaceKeyAction
  | DeleteWorkspaceKeyAction;

export const getWorkspaceKeys =
  (): ThunkAction<void, AppState, null, GetWorkspaceKeysAction> => async (dispatch, getState) => {
    if (getState().workspaceResources.workspaceKeys.allIds.length > 0) {
      dispatch({ type: WorkspaceKeysActionType.SILENT_GET_WORKSPACE_KEYS });
    } else {
      dispatch({ type: WorkspaceKeysActionType.GET_WORKSPACE_KEYS });
    }

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.getWorkspaceKeys({ workspaceId });
    if (response.ok) {
      dispatch({
        type: WorkspaceKeysActionType.GET_WORKSPACE_KEYS_OK,
        data: response.data as WorkspaceKey[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.GET_WORKSPACE_KEYS_ERROR,
      });
    }
  };

export const getWorkspaceKey =
  (keyId: string): ThunkAction<void, AppState, null, GetWorkspaceKeyAction> =>
  async (dispatch, getState) => {
    dispatch({ type: WorkspaceKeysActionType.GET_WORKSPACE_KEY, keyId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.getWorkspaceKey({ workspaceId, keyId });
    if (response.ok) {
      dispatch({
        keyId,
        type: WorkspaceKeysActionType.GET_WORKSPACE_KEY_OK,
        data: response.data as WorkspaceKey,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.GET_WORKSPACE_KEY_ERROR,
        keyId,
      });
    }
  };

export const createWorkspaceKey =
  (params: Partial<WorkspaceKey>): ThunkAction<void, AppState, null, CreateWorkspaceKeyAction> =>
  async (dispatch, getState) => {
    dispatch({ type: WorkspaceKeysActionType.CREATE_WORKSPACE_KEY });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.createWorkspaceKey({ workspaceId, params });
    if (response.ok) {
      dispatch({
        type: WorkspaceKeysActionType.CREATE_WORKSPACE_KEY_OK,
        data: response.data as WorkspaceKey,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.CREATE_WORKSPACE_KEY_ERROR,
      });
    }
  };

export const editWorkspaceKey =
  (
    keyId: string,
    params: Partial<WorkspaceKey>,
  ): ThunkAction<void, AppState, null, EditWorkspaceKeyAction> =>
  async (dispatch, getState) => {
    dispatch({ type: WorkspaceKeysActionType.EDIT_WORKSPACE_KEY, keyId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.editWorkspaceKey({ workspaceId, keyId, params });
    if (response.ok) {
      dispatch({
        keyId,
        type: WorkspaceKeysActionType.EDIT_WORKSPACE_KEY_OK,
        data: response.data as WorkspaceKey,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.EDIT_WORKSPACE_KEY_ERROR,
        keyId,
      });
    }
  };

export const deleteWorkspaceKeys =
  (): ThunkAction<void, AppState, null, DeleteWorkspaceKeysAction> =>
  async (dispatch, getState) => {
    dispatch({ type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.deleteWorkspaceKeys({ workspaceId });
    if (response.ok) {
      dispatch({
        type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS_OK,
        data: response.data as WorkspaceKey,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEYS_ERROR,
      });
    }
  };

export const deleteWorkspaceKey =
  (keyId: string): ThunkAction<void, AppState, null, DeleteWorkspaceKeyAction> =>
  async (dispatch, getState) => {
    dispatch({ type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEY, keyId });

    const workspaceId = selectCurrentWorkspaceId(getState());
    const response = await workspaceKeyService.deleteWorkspaceKey({ workspaceId, keyId });
    if (response.ok) {
      dispatch({
        keyId,
        type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEY_OK,
        data: response.data as WorkspaceKey,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: WorkspaceKeysActionType.DELETE_WORKSPACE_KEY_ERROR,
        keyId,
      });
    }
  };
